import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import ReactGA from 'react-ga4';
////////////////////////////////////////////////////////////////////
import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import { setActiveCollectionGroup } from '@/core/redux/slices';
import { slugify } from '@/app/shared/utils';
import { CollectionGrid } from './components';
import { BackButton } from '@/app/shared/components';

/**
 * Component for rendering a page of collections.
 *
 * @remarks
 * This component renders a page containing a list of collections, along with a
 * back button and a title. It also sends a ReactGA event when mounted.
 *
 * @returns A JSX element containing the collection grid.
 */
const Collections: React.FC = () => {
	/*********************
	// #region Hooks
	**********************/
	const { group } = useParams();
	const dispatch = useCustomDispatch();

	/*********************
	// #region Redux
	**********************/
	const { activeCollectionGroup } = useCustomSelector(
		(state) => state.collections
	);

	/*********************
	// #region Observables
	**********************/
	useEffect(() => {
		if (activeCollectionGroup) return;
		if (!group) return;
		dispatch(setActiveCollectionGroup(group));
	}, [group]);

	useEffect(() => {
		if (!activeCollectionGroup) return;
		ReactGA.send({
			hitType: 'pageview',
			page: `/collection/${slugify(activeCollectionGroup.name)}`,
			title: `Collection Group ${activeCollectionGroup.name}`,
		});
	}, [activeCollectionGroup]);

	/*********************
	// #region Template
	**********************/
	return (
		<>
			<div className="flex flex-col pt-8 gap-4 mx-4">
				<BackButton />
				<h2 className="text-center text-2xl font-bold">
					{activeCollectionGroup?.name}
				</h2>
				<div className="min-h-[162px]">
					<img
						className="mx-auto  border-none"
						src={process.env.VITE_OPEN_GIFT_IMAGE}
						alt={'Gift'}
					/>
				</div>
			</div>
			{activeCollectionGroup &&
				activeCollectionGroup.items.length > 0 && (
					<CollectionGrid collections={activeCollectionGroup.items} />
				)}
		</>
	);
};

export default Collections;
